import {
    CENTER_REGION_ID,
    DEFAULT_DOCK_SIZE,
    DEFAULT_EDGE_WRAP_ORDER,
    PANEL_KIND_CENTER,
    SPLIT_MIN_FRACTION,
    SPLIT_MAX_FRACTION,
    TAB_PLACEMENT_BOTTOM,
    TAB_PLACEMENT_TOP,
    defaultEdgeForRegion,
    normalizeDockZone,
} from './constants.js';
import {
    inferRegionsFromTree,
    resolveRegionRoot,
    findRegionBandRoot,
    findStructuralEdgeBand,
    subtreeHostsCenter,
    subtreeHostsZonePanel,
} from './layoutRegions.js';

let _nodeIdCounter = 0;

export function nextNodeId(prefix = 'n') {
    _nodeIdCounter += 1;
    return `${prefix}-${_nodeIdCounter}`;
}

export function cloneTree(node) {
    if (!node) return null;
    return JSON.parse(JSON.stringify(node));
}

export function normalizeSizes(sizes) {
    if (!sizes?.length) return [];
    const sum = sizes.reduce((a, b) => a + b, 0);
    if (sum <= 0) {
        const eq = 1 / sizes.length;
        return sizes.map(() => eq);
    }
    return sizes.map((s) => s / sum);
}

export function clampSplitPair(a, b) {
    let na = Math.min(SPLIT_MAX_FRACTION, Math.max(SPLIT_MIN_FRACTION, a));
    let nb = Math.min(SPLIT_MAX_FRACTION, Math.max(SPLIT_MIN_FRACTION, b));
    const sum = na + nb;
    na /= sum;
    nb /= sum;
    return [na, nb];
}

export function createPanelNode(panelId) {
    return { type: 'panel', id: nextNodeId('panel'), panelId };
}

export function createTabsNode(panelIds, activePanelId, options = {}) {
    const ids = panelIds.filter(Boolean);
    return {
        type: 'tabs',
        id: nextNodeId('tabs'),
        panelIds: ids,
        activePanelId: activePanelId || ids[0] || null,
        tabPlacement: options.tabPlacement || TAB_PLACEMENT_BOTTOM,
        title: options.title || null,
    };
}

export function createCenterSlotNode(child = null) {
    return {
        type: 'centerSlot',
        id: CENTER_REGION_ID,
        child: child ? cloneTree(child) : null,
    };
}

export function createSplitNode(orientation, children, sizes) {
    const ch = children.filter(Boolean);
    const sz =
        sizes?.length === ch.length
            ? normalizeSizes(sizes)
            : ch.map(() => 1 / ch.length);
    return {
        type: 'split',
        id: nextNodeId('split'),
        orientation,
        sizes: sz,
        children: ch,
    };
}

/**
 * Build a balanced split tree from panel ids.
 */
function buildZoneSubtree(panelIds, tabPlacement) {
    if (!panelIds.length) return null;
    if (panelIds.length === 1) return createPanelNode(panelIds[0]);
    return createTabsNode(panelIds, panelIds[0], { tabPlacement });
}

/**
 * Move region to end of wrap order (most recent = outermost on rebuild).
 */
export function bumpEdgeWrapOrder(order, region) {
    const r = normalizeDockZone(region);
    if (r === 'center') return [...(order || [])];
    const list = (order || []).filter((e) => normalizeDockZone(e) !== r);
    list.push(r);
    return list;
}

function isRegionBandChild(child, region, root, panelZoneMap) {
    if (!child) return false;
    const band = findRegionBandRoot(root, region, panelZoneMap);
    return band?.id === child.id;
}

function childContainsRegionBand(child, region, root, panelZoneMap) {
    if (!child) return false;
    if (isRegionBandChild(child, region, root, panelZoneMap)) return true;
    const band = findRegionBandRoot(root, region, panelZoneMap);
    if (!band?.id) return false;
    return subtreeContainsNodeId(child, band.id);
}

function subtreeContainsNodeId(node, targetId) {
    if (!node || !targetId) return false;
    if (node.id === targetId) return true;
    if (node.type === 'split') {
        return (node.children || []).some((ch) =>
            subtreeContainsNodeId(ch, targetId),
        );
    }
    if (node.type === 'centerSlot' && node.child) {
        return subtreeContainsNodeId(node.child, targetId);
    }
    return false;
}

/**
 * Infer chronological inner-to-outer edge wrap order from tree structure.
 */
export function inferEdgeWrapOrderFromTree(root, panelZoneMap = {}) {
    if (!root) return [...DEFAULT_EDGE_WRAP_ORDER];

    const outerToInner = [];
    let node = root;

    while (node?.type === 'split') {
        const children = node.children || [];
        if (children.length < 2) break;

        let peeled = false;

        if (node.orientation === 'vertical') {
            const first = children[0];
            const last = children[children.length - 1];
            if (isRegionBandChild(first, 'top', root, panelZoneMap)) {
                outerToInner.push('top');
                node = children.length === 2 ? children[1] : null;
                peeled = true;
            } else if (
                isRegionBandChild(last, 'bottom', root, panelZoneMap)
            ) {
                outerToInner.push('bottom');
                node = children.length === 2 ? children[0] : null;
                peeled = true;
            }
        }

        if (!peeled && node.orientation === 'horizontal') {
            const first = children[0];
            const last = children[children.length - 1];
            if (isRegionBandChild(first, 'left', root, panelZoneMap)) {
                outerToInner.push('left');
                node =
                    children.length === 2
                        ? children[1]
                        : children.length === 3
                          ? children[1]
                          : null;
                peeled = true;
            } else if (
                isRegionBandChild(last, 'right', root, panelZoneMap)
            ) {
                outerToInner.push('right');
                node = children.length === 2 ? children[0] : null;
                peeled = true;
            } else if (
                first?.type === 'split' &&
                childContainsRegionBand(first, 'left', root, panelZoneMap)
            ) {
                node = first;
                peeled = true;
            } else if (
                children.length === 3 &&
                childContainsRegionBand(last, 'right', root, panelZoneMap) &&
                childContainsRegionBand(first, 'left', root, panelZoneMap)
            ) {
                outerToInner.push('left');
                node = children[1];
                peeled = true;
            }
        }

        if (!peeled) break;
    }

    if (node?.type === 'panel' || node?.type === 'tabs') {
        for (const r of ['right', 'left', 'bottom', 'top']) {
            if (isRegionBandChild(node, r, root, panelZoneMap)) {
                outerToInner.push(r);
                break;
            }
        }
    }

    if (!outerToInner.length) return [...DEFAULT_EDGE_WRAP_ORDER];

    const chronological = outerToInner.reverse();
    const present = new Set(chronological);
    const merged = [];
    for (const r of DEFAULT_EDGE_WRAP_ORDER) {
        if (present.has(r)) merged.push(r);
    }
    for (const r of chronological) {
        if (!merged.includes(r)) merged.push(r);
    }
    return merged.length ? merged : [...DEFAULT_EDGE_WRAP_ORDER];
}

/**
 * Wrap core layout with a region band (same geometry as insertAtLayoutEdge).
 */
export function wrapLayoutAtEdge(region, bandSubtree, core) {
    const z = normalizeDockZone(region);
    if (!bandSubtree || z === 'center') return core;
    const sizes =
        z === 'left' || z === 'top'
            ? [DEFAULT_DOCK_SIZE, 1 - DEFAULT_DOCK_SIZE]
            : [1 - DEFAULT_DOCK_SIZE, DEFAULT_DOCK_SIZE];

    switch (z) {
        case 'left':
            return createSplitNode('horizontal', [bandSubtree, core], sizes);
        case 'right':
            return createSplitNode('horizontal', [core, bandSubtree], sizes);
        case 'top':
            return createSplitNode('vertical', [bandSubtree, core], sizes);
        case 'bottom':
            return createSplitNode('vertical', [core, bandSubtree], sizes);
        default:
            return core;
    }
}

/**
 * Outermost panel/tabs on an edge that does not contain the center region.
 */
function findOutermostEdgeLeafForPeel(tree, region, panelZoneMap) {
    const r = normalizeDockZone(region);
    if (!tree) return null;
    if (tree.type === 'panel' || tree.type === 'tabs') {
        return subtreeHostsZonePanel(tree, r, panelZoneMap) ? tree : null;
    }

    let node = tree;

    while (node?.type === 'split') {
        const horizontal =
            (r === 'left' || r === 'right') && node.orientation === 'horizontal';
        const vertical =
            (r === 'top' || r === 'bottom') && node.orientation === 'vertical';

        if (!horizontal && !vertical) {
            for (const ch of node.children || []) {
                if (!subtreeHostsZonePanel(ch, r, panelZoneMap)) continue;
                const nested = findOutermostEdgeLeafForPeel(ch, r, panelZoneMap);
                if (nested) return nested;
            }
            return null;
        }

        const idx =
            r === 'left' || r === 'top' ? 0 : (node.children?.length || 1) - 1;
        const child = node.children?.[idx];
        if (!child) return null;

        if (child.type === 'panel') {
            return subtreeHostsZonePanel(child, r, panelZoneMap) ? child : null;
        }
        if (child.type === 'tabs') {
            return subtreeHostsZonePanel(child, r, panelZoneMap) ? child : null;
        }
        if (subtreeHostsCenter(child)) {
            const nested = findOutermostEdgeLeafForPeel(child, r, panelZoneMap);
            if (nested) return nested;
            return null;
        }
        node = child;
    }

    return null;
}

/**
 * Detach one region band without pulling center (or other zones) into the band.
 */
function peelExclusiveRegionBand(tree, region, panelZoneMap) {
    const r = normalizeDockZone(region);
    if (!tree) return { tree, band: null };

    const band = findRegionBandRoot(tree, r, panelZoneMap);

    // Detach whole band splits in one step (e.g. right column). Single panels/tabs on an
    // edge that still hosts center elsewhere need composed peel so siblings are not orphaned.
    if (
        band?.id &&
        band.type === 'split' &&
        !subtreeHostsCenter(band)
    ) {
        const result = detachNode(tree, band.id);
        return { tree: result.tree, band: result.detached };
    }

    const parts = [];
    let t = tree;
    for (let i = 0; i < 32; i++) {
        const leaf = findOutermostEdgeLeafForPeel(t, r, panelZoneMap);
        if (!leaf?.id) break;
        const result = detachNode(t, leaf.id);
        if (!result.detached) break;
        parts.push(result.detached);
        t = result.tree;
    }

    if (!parts.length) return { tree, band: null };

    let merged = parts[0];
    for (let i = 1; i < parts.length; i++) {
        merged = wrapLayoutAtEdge(r, parts[i], merged);
    }
    return { tree: t, band: merged };
}

/**
 * Extract mutually exclusive region band subtrees by peeling outer wraps first.
 * (Resolving every band against the full tree duplicates nested content on reassemble.)
 */
export function extractRegionSubtrees(
    root,
    regions,
    panelZoneMap = {},
    edgeWrapOrder = DEFAULT_EDGE_WRAP_ORDER,
) {
    const subtrees = {
        left: null,
        right: null,
        top: null,
        bottom: null,
        center: null,
    };

    if (!root) return subtrees;

    let tree = cloneTree(root);
    const peelOrder = [...(edgeWrapOrder || DEFAULT_EDGE_WRAP_ORDER)].reverse();

    for (const region of peelOrder) {
        const r = normalizeDockZone(region);
        if (r === 'center') continue;

        const { tree: nextTree, band } = peelExclusiveRegionBand(
            tree,
            r,
            panelZoneMap,
        );
        if (!band) continue;

        subtrees[r] = band;
        tree = nextTree;
    }

    if (!tree) return subtrees;

    const centerBand = findRegionBandRoot(tree, 'center', panelZoneMap);
    if (centerBand) {
        subtrees.center = cloneTree(centerBand);
    } else {
        const slot = findNode(tree, CENTER_REGION_ID)?.node;
        if (slot?.type === 'centerSlot') {
            subtrees.center = slot.child ? cloneTree(slot.child) : null;
        } else if (tree.type === 'centerSlot') {
            subtrees.center = tree.child ? cloneTree(tree.child) : null;
        } else {
            subtrees.center = cloneTree(tree);
        }
    }

    return subtrees;
}

/**
 * Assemble layout from region subtrees; edgeWrapOrder is chronological (inner → outer).
 */
export function assembleLayoutFromRegionSubtrees(
    regionSubtrees = {},
    edgeWrapOrder = DEFAULT_EDGE_WRAP_ORDER,
) {
    const regions = {
        left: null,
        right: null,
        top: null,
        bottom: null,
        center: CENTER_REGION_ID,
    };

    let core = createCenterSlotNode(regionSubtrees.center || null);
    if (regionSubtrees.center) {
        regions.center = regionSubtrees.center.id;
    }

    const order = edgeWrapOrder || DEFAULT_EDGE_WRAP_ORDER;
    for (let i = 0; i < order.length; i++) {
        const r = normalizeDockZone(order[i]);
        if (r === 'center') continue;
        const band = regionSubtrees[r];
        if (!band) continue;
        core = wrapLayoutAtEdge(r, cloneTree(band), core);
        regions[r] = band.id;
    }

    return { root: core, regions };
}

/**
 * Rebuild outer skeleton so edgeWrapOrder determines full-width / full-height bands.
 */
export function rebuildLayoutWithEdgeOrder(
    root,
    regions,
    panelZoneMap = {},
    edgeWrapOrder = DEFAULT_EDGE_WRAP_ORDER,
) {
    if (!root) return { root: null, regions: regions || {} };
    const subtrees = extractRegionSubtrees(
        root,
        regions,
        panelZoneMap,
        edgeWrapOrder,
    );
    const assembled = assembleLayoutFromRegionSubtrees(subtrees, edgeWrapOrder);
    return {
        root: pruneTree(assembled.root),
        regions: assembled.regions,
    };
}

/**
 * VS-style skeleton from panel zone assignments.
 * @returns {{ root, regions }}
 */
export function buildLayoutFromPanels(panels) {
    const list = (panels || []).filter((p) => p?.id);
    if (!list.length) {
        return {
            root: null,
            regions: {
                left: null,
                right: null,
                top: null,
                bottom: null,
                center: CENTER_REGION_ID,
            },
        };
    }

    const byZone = { left: [], right: [], top: [], bottom: [], center: [] };
    for (const p of list) {
        const z =
            p.zone != null && p.zone !== ''
                ? normalizeDockZone(p.zone)
                : 'center';
        byZone[z].push(p.id);
    }

    const subtrees = {
        left: buildZoneSubtree(byZone.left, TAB_PLACEMENT_BOTTOM),
        right: buildZoneSubtree(byZone.right, TAB_PLACEMENT_BOTTOM),
        top: buildZoneSubtree(byZone.top, TAB_PLACEMENT_BOTTOM),
        bottom: buildZoneSubtree(byZone.bottom, TAB_PLACEMENT_BOTTOM),
        center: null,
    };

    if (byZone.center.length === 1) {
        subtrees.center = createPanelNode(byZone.center[0]);
    } else if (byZone.center.length > 1) {
        subtrees.center = createTabsNode(byZone.center, byZone.center[0], {
            tabPlacement: TAB_PLACEMENT_TOP,
        });
    }

    return assembleLayoutFromRegionSubtrees(subtrees, DEFAULT_EDGE_WRAP_ORDER);
}

/**
 * VS-style skeleton using pre-built subtrees per region (from blueprint).
 * @param {Record<string, object|null>} regionSubtrees — left|right|top|bottom|center
 */
export function buildLayoutFromRegionSubtrees(regionSubtrees = {}) {
    return assembleLayoutFromRegionSubtrees(
        regionSubtrees,
        DEFAULT_EDGE_WRAP_ORDER,
    );
}

/** @deprecated use buildLayoutFromPanels */
export function buildTreeFromPanelZones(panels) {
    return buildLayoutFromPanels(panels).root;
}

/**
 * Detach an entire subtree (tab group or panel) by node id.
 */
export function detachNode(root, nodeId) {
    if (!root || !nodeId) {
        return { tree: root, detached: null };
    }
    if (root.id === nodeId) {
        return { tree: null, detached: cloneTree(root) };
    }

    const parentInfo = findParentOfNode(root, nodeId);
    if (!parentInfo) {
        return { tree: root, detached: null };
    }

    const parent = parentInfo.parent;
    let detached;
    let newTree;

    if (parent.type === 'centerSlot') {
        detached = cloneTree(parent.child);
        newTree = replaceNode(root, parent.id, { ...parent, child: null });
    } else if (parent.type === 'split') {
        detached = cloneTree(parent.children[parentInfo.index]);
        newTree = removeChildAt(root, parent.id, parentInfo.index);
    } else {
        return { tree: root, detached: null };
    }

    return { tree: pruneTree(newTree), detached };
}

/**
 * Insert detached subtree into layout at region (top-level drop).
 */
export function insertSubtreeIntoRegion(
    root,
    regions,
    region,
    subtree,
    panelZoneMap = {},
) {
    const r = normalizeDockZone(region);
    if (!subtree) return { root, regions };

    const nextRegions = { ...regions, [r]: subtree.id };

    if (!root) {
        if (r === 'center') {
            return {
                root: createCenterSlotNode(subtree),
                regions: { ...nextRegions, center: subtree.id },
            };
        }
        return { root: subtree, regions: nextRegions };
    }

    const regionRoot = resolveRegionRoot(root, regions, r, panelZoneMap);

    if (!regionRoot) {
        if (r === 'center') {
            const slot = createCenterSlotNode(subtree);
            return {
                root: replaceCenterInTree(root, slot),
                regions: { ...nextRegions, center: subtree.id },
            };
        }
        return insertAtLayoutEdge(root, regions, r, subtree);
    }

    const edge = defaultEdgeForRegion(r);
    const merged = mergeSubtreesAtEdge(regionRoot, subtree, edge);
    let newRoot = replaceNode(root, regionRoot.id, merged);
    nextRegions[r] = merged.id;
    return { root: pruneTree(newRoot), regions: nextRegions };
}

function replaceCenterInTree(root, centerSlot) {
    if (!root) return centerSlot;
    if (root.type === 'centerSlot' || root.id === CENTER_REGION_ID) {
        return centerSlot;
    }
    if (root.type === 'split') {
        return {
            ...root,
            children: root.children.map((ch) =>
                replaceCenterInTree(ch, centerSlot),
            ),
        };
    }
    return root;
}

function mergeSubtreesAtEdge(existing, incoming, edge) {
    const z = normalizeDockZone(edge);
    if (z === 'center') {
        if (existing.type === 'tabs' && incoming.type === 'tabs') {
            return {
                ...existing,
                panelIds: [
                    ...existing.panelIds,
                    ...incoming.panelIds.filter(
                        (id) => !existing.panelIds.includes(id),
                    ),
                ],
                activePanelId: incoming.activePanelId || existing.activePanelId,
            };
        }
        if (existing.type === 'panel' && incoming.type === 'panel') {
            return createTabsNode(
                [existing.panelId, incoming.panelId],
                incoming.panelId,
                { tabPlacement: TAB_PLACEMENT_BOTTOM },
            );
        }
        if (existing.type === 'tabs' && incoming.type === 'panel') {
            const panelIds = existing.panelIds || [];
            if (panelIds.includes(incoming.panelId)) {
                return { ...existing, activePanelId: incoming.panelId };
            }
            return {
                ...existing,
                panelIds: [...panelIds, incoming.panelId],
                activePanelId: incoming.panelId,
            };
        }
        if (existing.type === 'panel' && incoming.type === 'tabs') {
            const panelIds = incoming.panelIds || [];
            if (panelIds.includes(existing.panelId)) {
                return { ...incoming, activePanelId: existing.panelId };
            }
            return {
                ...incoming,
                panelIds: [existing.panelId, ...panelIds],
                activePanelId: existing.panelId,
            };
        }
        return incoming;
    }

    const orientation =
        z === 'left' || z === 'right' ? 'horizontal' : 'vertical';
    const sizes =
        z === 'left' || z === 'top'
            ? [DEFAULT_DOCK_SIZE, 1 - DEFAULT_DOCK_SIZE]
            : [1 - DEFAULT_DOCK_SIZE, DEFAULT_DOCK_SIZE];

    return createSplitNode(
        orientation,
        z === 'left' || z === 'top'
            ? [incoming, existing]
            : [existing, incoming],
        sizes,
    );
}

function insertAtLayoutEdge(root, regions, region, subtree) {
    const z = normalizeDockZone(region);
    const sizes =
        z === 'left' || z === 'top'
            ? [DEFAULT_DOCK_SIZE, 1 - DEFAULT_DOCK_SIZE]
            : [1 - DEFAULT_DOCK_SIZE, DEFAULT_DOCK_SIZE];

    switch (z) {
        case 'left':
            return {
                root: createSplitNode('horizontal', [subtree, root], sizes),
                regions: { ...regions, left: subtree.id },
            };
        case 'right':
            return {
                root: createSplitNode('horizontal', [root, subtree], sizes),
                regions: { ...regions, right: subtree.id },
            };
        case 'top':
            return {
                root: createSplitNode('vertical', [subtree, root], sizes),
                regions: { ...regions, top: subtree.id },
            };
        case 'bottom':
            return {
                root: createSplitNode('vertical', [root, subtree], sizes),
                regions: { ...regions, bottom: subtree.id },
            };
        case 'center':
            return {
                root: createCenterSlotNode(subtree),
                regions: { ...regions, center: subtree.id },
            };
        default:
            return { root, regions };
    }
}

/**
 * Top-level region drop: dock panel into layout region band.
 */
export function dockPanelToRegion(
    root,
    regions,
    panelId,
    region,
    edgeZone,
    panelZoneMap = {},
) {
    const r = normalizeDockZone(region);
    const edge = normalizeDockZone(edgeZone || defaultEdgeForRegion(r));

    let tree = cloneTree(root);
    const reg = { ...(regions || createEmptyRegionsFromConstants()) };

    let regionRoot = resolveRegionRoot(tree, reg, r, panelZoneMap);

    if (r === 'center' && !regionRoot) {
        const slot = findNode(tree, CENTER_REGION_ID)?.node;
        if (slot?.type === 'centerSlot') {
            regionRoot = slot.child;
        }
    }

    if (regionRoot?.id && isDockDropNoop(tree, panelId, regionRoot.id, edge)) {
        return {
            root: tree,
            regions: syncRegionsAfterChange(tree, panelZoneMap),
        };
    }

    const detached = detachPanel(tree, panelId);
    tree = detached.tree;

    regionRoot = resolveRegionRoot(tree, reg, r, panelZoneMap);
    if (r === 'center' && !regionRoot) {
        const slot = findNode(tree, CENTER_REGION_ID)?.node;
        if (slot?.type === 'centerSlot') {
            regionRoot = slot.child;
        }
    }

    if (!regionRoot) {
        const band = findRegionBandRoot(tree, r, panelZoneMap);
        if (band) {
            const docked = dockPanelAtTarget(tree, panelId, band.id, edge);
            return {
                root: docked,
                regions: syncRegionsAfterChange(docked, panelZoneMap),
            };
        }

        const panelNode = createPanelNode(panelId);
        if (r === 'center') {
            const slot = createCenterSlotNode(panelNode);
            const newRoot = tree
                ? replaceCenterInTree(tree, slot)
                : slot;
            reg.center = panelNode.id;
            return {
                root: newRoot,
                regions: syncRegionsAfterChange(newRoot, panelZoneMap),
            };
        }
        const inserted = insertSubtreeIntoRegion(
            tree,
            reg,
            r,
            panelNode,
            panelZoneMap,
        );
        return {
            root: inserted.root,
            regions: syncRegionsAfterChange(inserted.root, panelZoneMap),
        };
    }

    const panelNode = createPanelNode(panelId);
    const merged = mergeSubtreesAtEdge(regionRoot, panelNode, edge);
    const newRoot = replaceNode(tree, regionRoot.id, merged);
    return {
        root: newRoot,
        regions: syncRegionsAfterChange(newRoot, panelZoneMap),
    };
}

function createEmptyRegionsFromConstants() {
    return {
        left: null,
        right: null,
        top: null,
        bottom: null,
        center: CENTER_REGION_ID,
    };
}

/**
 * Dock into center region (including empty center slot).
 */
export function dockPanelToCenterRegion(root, regions, panelId, panelZoneMap = {}) {
    return dockPanelToRegion(
        root,
        regions,
        panelId,
        'center',
        'center',
        panelZoneMap,
    );
}

export function setCenterSlotChild(root, child) {
    if (!root) return child ? createCenterSlotNode(child) : null;
    const slot = findNode(root, CENTER_REGION_ID)?.node;
    if (slot?.type === 'centerSlot') {
        return replaceNode(root, slot.id, {
            ...slot,
            child: child ? cloneTree(child) : null,
        });
    }
    return root;
}

export function treeContainsPanel(root, panelId) {
    return collectPanelIds(root).includes(panelId);
}

function dockCenterSubtree(tree, panelId) {
    if (!tree) return createPanelNode(panelId);
    if (tree.type === 'panel') {
        if (tree.panelId === panelId) return tree;
        return createTabsNode([tree.panelId, panelId], panelId);
    }
    if (tree.type === 'tabs') {
        if (tree.panelIds.includes(panelId)) {
            return { ...tree, activePanelId: panelId };
        }
        return {
            ...tree,
            panelIds: [...tree.panelIds, panelId],
            activePanelId: panelId,
        };
    }
    if (tree.type === 'split' && tree.children?.length) {
        let maxIdx = 0;
        let maxSize = tree.sizes?.[0] ?? 0;
        for (let i = 1; i < tree.children.length; i++) {
            const s = tree.sizes?.[i] ?? 0;
            if (s > maxSize) {
                maxSize = s;
                maxIdx = i;
            }
        }
        const children = [...tree.children];
        children[maxIdx] = dockCenterSubtree(children[maxIdx], panelId);
        return { ...tree, children };
    }
    return tree;
}

/**
 * Dock a panel relative to the entire layout root (not a single panel node).
 */
export function dockPanelToLayoutZone(root, panelId, zone) {
    const z = normalizeDockZone(zone);
    let tree = cloneTree(root);
    const detached = detachPanel(tree, panelId);
    tree = detached.tree;

    if (!tree) {
        return createPanelNode(panelId);
    }

    const sizes =
        z === 'left' || z === 'top'
            ? [DEFAULT_DOCK_SIZE, 1 - DEFAULT_DOCK_SIZE]
            : [1 - DEFAULT_DOCK_SIZE, DEFAULT_DOCK_SIZE];

    switch (z) {
        case 'left':
            return createSplitNode(
                'horizontal',
                [createPanelNode(panelId), tree],
                sizes,
            );
        case 'right':
            return createSplitNode(
                'horizontal',
                [tree, createPanelNode(panelId)],
                sizes,
            );
        case 'top':
            return createSplitNode(
                'vertical',
                [createPanelNode(panelId), tree],
                sizes,
            );
        case 'bottom':
            return createSplitNode(
                'vertical',
                [tree, createPanelNode(panelId)],
                sizes,
            );
        case 'center':
        default:
            return dockCenterSubtree(tree, panelId);
    }
}

export function buildDefaultTree(panelIds, orientation = 'horizontal') {
    const ids = [...panelIds].filter(Boolean);
    if (ids.length === 0) return null;
    if (ids.length === 1) return createPanelNode(ids[0]);

    const mid = Math.ceil(ids.length / 2);
    const left = ids.slice(0, mid);
    const right = ids.slice(mid);

    const buildHalf = (half) => {
        if (half.length === 1) return createPanelNode(half[0]);
        return buildDefaultTree(half, orientation);
    };

    return createSplitNode(
        orientation,
        [buildHalf(left), buildHalf(right)],
        [0.5, 0.5],
    );
}

/**
 * Find a node by id in the tree. Returns { node, parent, index, path }.
 */
export function findNode(root, nodeId, parent = null, index = -1, path = []) {
    if (!root) return null;
    if (root.id === nodeId) {
        return { node: root, parent, index, path: [...path, root] };
    }
    if (root.type === 'centerSlot' && root.child) {
        const found = findNode(root.child, nodeId, root, 0, [...path, root]);
        if (found) return found;
    }
    if (root.type === 'split' && root.children) {
        for (let i = 0; i < root.children.length; i++) {
            const found = findNode(root.children[i], nodeId, root, i, [...path, root]);
            if (found) return found;
        }
    }
    return null;
}

export function findPanelHost(root, panelId) {
    if (!root) return null;

    if (root.type === 'panel' && root.panelId === panelId) {
        return { host: root, hostType: 'panel', tabsNode: null };
    }

    if (root.type === 'tabs') {
        const idx = root.panelIds.indexOf(panelId);
        if (idx >= 0) {
            return { host: root, hostType: 'tabs', tabsNode: root, tabIndex: idx };
        }
    }

    if (root.type === 'centerSlot' && root.child) {
        const found = findPanelHost(root.child, panelId);
        if (found) return found;
    }

    if (root.type === 'split' && root.children) {
        for (const child of root.children) {
            const found = findPanelHost(child, panelId);
            if (found) return found;
        }
    }

    return null;
}

export function collectPanelIds(root, out = []) {
    if (!root) return out;
    if (root.type === 'panel' && root.panelId) {
        out.push(root.panelId);
    } else if (root.type === 'tabs') {
        for (const id of root.panelIds || []) out.push(id);
    } else if (root.type === 'centerSlot' && root.child) {
        collectPanelIds(root.child, out);
    } else if (root.type === 'split') {
        for (const child of root.children || []) collectPanelIds(child, out);
    }
    return out;
}

/**
 * Replace a node in the tree by id.
 */
export function replaceNode(root, nodeId, replacement) {
    if (!root) return replacement;
    if (root.id === nodeId) return replacement;

    if (root.type === 'centerSlot') {
        if (!root.child) return root;
        if (root.child.id === nodeId) {
            return { ...root, child: replacement };
        }
        const child = replaceNode(root.child, nodeId, replacement);
        if (child !== root.child) {
            return { ...root, child };
        }
        return root;
    }

    if (root.type === 'split') {
        const children = root.children.map((ch) => {
            if (ch.id === nodeId) return replacement;
            return replaceNode(ch, nodeId, replacement);
        });
        return { ...root, children };
    }

    return root;
}

/**
 * Remove panel from tree; returns { tree, removed, lastDock }.
 */
export function detachPanel(root, panelId) {
    if (!root) return { tree: null, removed: false, lastDock: null };

    const hostInfo = findPanelHost(root, panelId);
    if (!hostInfo) return { tree: root, removed: false, lastDock: null };

    const lastDock = captureLastDock(root, hostInfo);

    if (hostInfo.hostType === 'panel') {
        const parentInfo = findParentOfNode(root, hostInfo.host.id);
        if (!parentInfo) {
            return { tree: null, removed: true, lastDock };
        }
        let newTree;
        if (parentInfo.parent.type === 'centerSlot') {
            newTree = replaceNode(root, parentInfo.parent.id, {
                ...parentInfo.parent,
                child: null,
            });
        } else {
            newTree = removeChildAt(root, parentInfo.parent.id, parentInfo.index);
        }
        return { tree: pruneTree(newTree), removed: true, lastDock };
    }

    if (hostInfo.hostType === 'tabs') {
        const tabs = hostInfo.host;
        const newIds = tabs.panelIds.filter((id) => id !== panelId);
        let replacement;
        if (newIds.length === 0) {
            replacement = null;
        } else         if (newIds.length === 1) {
            replacement = createPanelNode(newIds[0]);
        } else {
            replacement = {
                ...tabs,
                panelIds: newIds,
                activePanelId:
                    tabs.activePanelId === panelId
                        ? newIds[0]
                        : tabs.activePanelId,
                tabPlacement: tabs.tabPlacement || TAB_PLACEMENT_BOTTOM,
            };
        }
        const newTree = replaceNode(root, tabs.id, replacement);
        return { tree: pruneTree(newTree), removed: true, lastDock };
    }

    return { tree: root, removed: false, lastDock: null };
}

function findParentOfNode(root, childId, parent = null) {
    if (!root) return null;
    if (root.type === 'centerSlot' && root.child) {
        if (root.child.id === childId) {
            return { parent: root, index: 0 };
        }
        const found = findParentOfNode(root.child, childId, root);
        if (found) return found;
    }
    if (root.type === 'split') {
        for (let i = 0; i < root.children.length; i++) {
            if (root.children[i].id === childId) {
                return { parent: root, index: i };
            }
            const found = findParentOfNode(root.children[i], childId, root);
            if (found) return found;
        }
    }
    return null;
}

function removeChildAt(root, parentId, childIndex) {
    const parentInfo = findNode(root, parentId);
    if (!parentInfo?.node) return root;

    if (parentInfo.node.type === 'centerSlot') {
        return replaceNode(root, parentId, { ...parentInfo.node, child: null });
    }

    if (parentInfo.node.type !== 'split') return root;

    const parent = parentInfo.node;
    const children = parent.children.filter((_, i) => i !== childIndex);
    const sizes = parent.sizes.filter((_, i) => i !== childIndex);

    let replacement;
    if (children.length === 0) {
        replacement = null;
    } else if (children.length === 1) {
        replacement = children[0];
    } else {
        replacement = {
            ...parent,
            children,
            sizes: normalizeSizes(sizes),
        };
    }

    return replaceNode(root, parentId, replacement);
}

export function pruneTree(root) {
    if (!root) return null;
    if (root.type === 'centerSlot') {
        if (!root.child) return { ...root, child: null };
        const child = pruneTree(root.child);
        if (!child) return { ...root, child: null };
        return { ...root, child };
    }
    if (root.type === 'split') {
        const children = root.children.map(pruneTree).filter(Boolean);
        if (children.length === 0) return null;
        if (children.length === 1) return children[0];
        const sizes =
            root.sizes.length === children.length
                ? normalizeSizes(root.sizes.slice(0, children.length))
                : children.map(() => 1 / children.length);
        return { ...root, children, sizes };
    }
    if (root.type === 'tabs') {
        if (!root.panelIds?.length) return null;
        if (root.panelIds.length === 1) {
            return createPanelNode(root.panelIds[0]);
        }
        return root;
    }
    if (root.type === 'panel') return root;
    return root;
}

function selectSplitChildForEdge(split, zone) {
    const children = split.children || [];
    if (!children.length) return null;
    const z = normalizeDockZone(zone);

    if (split.orientation === 'horizontal') {
        switch (z) {
            case 'left':
                return children[0];
            case 'right':
                return children[children.length - 1];
            default:
                return null;
        }
    }
    if (split.orientation === 'vertical') {
        switch (z) {
            case 'top':
                return children[0];
            case 'bottom':
                return children[children.length - 1];
            default:
                return null;
        }
    }
    return null;
}

function captureLastDock(root, hostInfo) {
    const path = [];
    let current = root;
    const walk = (node, targetId) => {
        if (!node) return false;
        path.push({ nodeId: node.id, type: node.type });
        if (node.id === targetId) return true;
        if (node.type === 'split') {
            for (const ch of node.children) {
                if (walk(ch, targetId)) return true;
            }
        }
        path.pop();
        return false;
    };
    walk(root, hostInfo.host.id);
    return {
        hostNodeId: hostInfo.host.id,
        hostType: hostInfo.hostType,
        path,
    };
}

/**
 * Insert panel relative to target node and zone.
 */
export function isDockDropNoop(root, panelId, targetNodeId, zone) {
    if (!root || !panelId || !targetNodeId) return false;
    if (normalizeDockZone(zone) !== 'center') return false;

    const target = findNode(root, targetNodeId)?.node;
    if (!target) return false;

    if (target.type === 'panel' && target.panelId === panelId) {
        return true;
    }
    if (target.type === 'tabs' && target.panelIds?.includes(panelId)) {
        return true;
    }
    return false;
}

export function dockPanelAtTarget(root, panelId, targetNodeId, zone) {
    if (!root || !panelId || !targetNodeId || !zone) {
        return root;
    }
    return movePanelToZone(root, panelId, targetNodeId, zone);
}

export function movePanelToZone(root, panelId, targetNodeId, zone) {
    if (!root || !panelId || !targetNodeId || !zone) {
        return root;
    }

    if (isDockDropNoop(root, panelId, targetNodeId, zone)) {
        return cloneTree(root);
    }

    let tree = cloneTree(root);
    const originalTarget = findNode(tree, targetNodeId)?.node;
    const detachedFromTargetTabs =
        originalTarget?.type === 'tabs' &&
        (originalTarget.panelIds || []).includes(panelId);
    const targetParentBeforeDetach = detachedFromTargetTabs
        ? findParentOfNode(tree, targetNodeId)
        : null;

    const detached = detachPanel(tree, panelId);
    tree = detached.tree;

    let resolvedTargetId = targetNodeId;
    if (detachedFromTargetTabs && detached.removed && targetParentBeforeDetach) {
        const parentNode = findNode(
            tree,
            targetParentBeforeDetach.parent.id,
        )?.node;
        const child = parentNode?.children?.[targetParentBeforeDetach.index];
        if (child?.id) resolvedTargetId = child.id;
    }

    const targetInfo = findNode(tree, resolvedTargetId);
    if (!targetInfo?.node) {
        if (!tree) return createPanelNode(panelId);
        return appendPanelToRoot(tree, panelId);
    }

    const target = targetInfo.node;

    if (target.type === 'split') {
        const child = selectSplitChildForEdge(target, zone);
        if (child) {
            return movePanelToZone(tree, panelId, child.id, zone);
        }
    }

    if (target.type === 'centerSlot') {
        if (zone === 'center') {
            const child = target.child;
            if (!child) {
                const panelNode = createPanelNode(panelId);
                return replaceNode(tree, target.id, {
                    ...target,
                    child: panelNode,
                });
            }
            return movePanelToZone(
                tree,
                panelId,
                child.id,
                'center',
            );
        }
        if (target.child) {
            return movePanelToZone(tree, panelId, target.child.id, zone);
        }
        const panelNode = createPanelNode(panelId);
        return replaceNode(tree, target.id, { ...target, child: panelNode });
    }

    if (zone === 'center') {
        if (target.type === 'tabs') {
            const newTabs = {
                ...target,
                panelIds: [...target.panelIds, panelId],
                activePanelId: panelId,
            };
            return replaceNode(tree, target.id, newTabs);
        }
        if (target.type === 'panel') {
            const newTabs = createTabsNode([target.panelId, panelId], panelId);
            return replaceNode(tree, target.id, newTabs);
        }
        return tree;
    }

    const splitOrientation =
        zone === 'left' || zone === 'right' ? 'horizontal' : 'vertical';

    const panelNode = createPanelNode(panelId);
    let newTargetSide = panelNode;
    let existingSide = target;

    if (zone === 'right' || zone === 'bottom') {
        newTargetSide = target;
        existingSide = panelNode;
    }

    const sizes =
        zone === 'left' || zone === 'top'
            ? [DEFAULT_DOCK_SIZE, 1 - DEFAULT_DOCK_SIZE]
            : [1 - DEFAULT_DOCK_SIZE, DEFAULT_DOCK_SIZE];

    const newSplit = createSplitNode(
        splitOrientation,
        zone === 'left' || zone === 'top'
            ? [panelNode, target]
            : [target, panelNode],
        sizes,
    );

    return replaceNode(tree, target.id, newSplit);
}

export function joinPanelsAsTabs(root, sourcePanelId, targetPanelId) {
    if (!root || sourcePanelId === targetPanelId) return root;

    let tree = cloneTree(root);
    const targetHost = findPanelHost(tree, targetPanelId);
    if (!targetHost) return root;

    const detached = detachPanel(tree, sourcePanelId);
    tree = detached.tree;

    const targetInfo = findPanelHost(tree, targetPanelId);
    if (!targetInfo) {
        if (!tree) return createPanelNode(sourcePanelId);
        return tree;
    }

    if (targetInfo.hostType === 'tabs') {
        const tabs = targetInfo.host;
        const newTabs = {
            ...tabs,
            panelIds: [...tabs.panelIds, sourcePanelId],
            activePanelId: sourcePanelId,
        };
        return replaceNode(tree, tabs.id, newTabs);
    }

    if (targetInfo.hostType === 'panel') {
        const newTabs = createTabsNode(
            [targetPanelId, sourcePanelId],
            sourcePanelId,
        );
        return replaceNode(tree, targetInfo.host.id, newTabs);
    }

    return tree;
}

export function setSplitSizes(root, splitId, sizes) {
    const info = findNode(root, splitId);
    if (!info?.node || info.node.type !== 'split') return root;
    const updated = { ...info.node, sizes: normalizeSizes(sizes) };
    return replaceNode(root, splitId, updated);
}

export function setActiveTab(root, tabsId, panelId) {
    const info = findNode(root, tabsId);
    if (!info?.node || info.node.type !== 'tabs') return root;
    if (!info.node.panelIds.includes(panelId)) return root;
    return replaceNode(root, tabsId, { ...info.node, activePanelId: panelId });
}

export function reorderTabPanels(root, tabsId, panelIds, activePanelId) {
    const info = findNode(root, tabsId);
    if (!info?.node || info.node.type !== 'tabs') return root;
    return replaceNode(root, tabsId, {
        ...info.node,
        panelIds,
        activePanelId: activePanelId || panelIds[0],
    });
}

export function moveTabBetweenGroups(
    root,
    sourceTabsId,
    targetTabsId,
    panelId,
    targetIndex,
) {
    if (!root) return root;
    let tree = cloneTree(root);

    const sourceInfo = findNode(tree, sourceTabsId);
    const targetInfo = findNode(tree, targetTabsId);
    if (!sourceInfo?.node || sourceInfo.node.type !== 'tabs') return tree;

    const sourceTabs = sourceInfo.node;
    if (!sourceTabs.panelIds.includes(panelId)) return tree;

    const newSourceIds = sourceTabs.panelIds.filter((id) => id !== panelId);
    let sourceReplacement;
    if (newSourceIds.length === 0) sourceReplacement = null;
    else if (newSourceIds.length === 1) {
        sourceReplacement = createPanelNode(newSourceIds[0]);
    } else {
        sourceReplacement = {
            ...sourceTabs,
            panelIds: newSourceIds,
            activePanelId:
                sourceTabs.activePanelId === panelId
                    ? newSourceIds[0]
                    : sourceTabs.activePanelId,
        };
    }
    tree = replaceNode(tree, sourceTabsId, sourceReplacement);
    tree = pruneTree(tree);

    if (!targetInfo?.node) return tree;

    if (targetInfo.node.type === 'tabs' && targetTabsId !== sourceTabsId) {
        const targetTabs = findNode(tree, targetTabsId)?.node;
        if (!targetTabs || targetTabs.type !== 'tabs') return tree;
        const ids = [...targetTabs.panelIds];
        const idx = Math.max(0, Math.min(targetIndex ?? ids.length, ids.length));
        ids.splice(idx, 0, panelId);
        return replaceNode(tree, targetTabsId, {
            ...targetTabs,
            panelIds: ids,
            activePanelId: panelId,
        });
    }

    return tree;
}

/**
 * Remove panel from tree without adding to hiddenPanelIds (ephemeral / dynamic tabs).
 */
export function closePanelInState(state, panelId) {
    const detached = detachPanel(cloneTree(state.root), panelId);
    const panels = { ...(state.panels || {}) };
    delete panels[panelId];
    return {
        ...state,
        root: detached.tree,
        panels,
    };
}

export function appendPanelToTabsGroup(
    root,
    tabsId,
    panelId,
    activate = true,
) {
    const info = findNode(root, tabsId);
    if (!info?.node || info.node.type !== 'tabs') return root;
    const ids = [...info.node.panelIds];
    if (ids.includes(panelId)) {
        if (!activate) return root;
        return setActiveTab(root, tabsId, panelId);
    }
    ids.push(panelId);
    return replaceNode(root, tabsId, {
        ...info.node,
        panelIds: ids,
        activePanelId: activate ? panelId : info.node.activePanelId,
    });
}

export function findCenterDynamicTabsNode(root) {
    const slot = findNode(root, CENTER_REGION_ID)?.node;
    let found = null;
    walkLayoutNodes(slot?.child, (n) => {
        if (n.type === 'tabs' && n.contentMode === 'dynamic') {
            found = n;
        }
    });
    return found;
}

function walkLayoutNodes(node, fn) {
    if (!node) return;
    fn(node);
    if (node.type === 'split') {
        for (const ch of node.children || []) walkLayoutNodes(ch, fn);
    }
    if (node.type === 'centerSlot' && node.child) {
        walkLayoutNodes(node.child, fn);
    }
}

export function hidePanelInState(state, panelId) {
    const root = state.root;
    const detached = detachPanel(cloneTree(root), panelId);
    const hiddenPanelIds = [...(state.hiddenPanelIds || [])];
    if (!hiddenPanelIds.includes(panelId)) hiddenPanelIds.push(panelId);

    const panels = { ...(state.panels || {}) };
    if (detached.lastDock) {
        panels[panelId] = {
            ...(panels[panelId] || {}),
            lastDock: detached.lastDock,
        };
    }

    return {
        ...state,
        root: detached.tree,
        hiddenPanelIds,
        panels,
    };
}

export function showPanelInState(state, panelId) {
    const hiddenPanelIds = (state.hiddenPanelIds || []).filter(
        (id) => id !== panelId,
    );
    const panels = { ...(state.panels || {}) };
    const meta = panels[panelId] || {};
    let root = cloneTree(state.root);

    if (!root) {
        root = createPanelNode(panelId);
    } else {
        const lastDock = meta.lastDock;
        if (lastDock?.nodeId && lastDock?.zone) {
            if (lastDock.layout || lastDock.nodeId === '__mp-dock-root__') {
                root = dockPanelToLayoutZone(root, panelId, lastDock.zone);
            } else {
                root = dockPanelAtTarget(
                    root,
                    panelId,
                    lastDock.nodeId,
                    lastDock.zone,
                );
            }
        } else if (lastDock?.layout && lastDock?.zone) {
            root = dockPanelToLayoutZone(root, panelId, lastDock.zone);
        } else if (lastDock?.hostNodeId) {
            const host = findNode(root, lastDock.hostNodeId);
            if (host?.node) {
                root = movePanelToZone(
                    root,
                    panelId,
                    lastDock.hostNodeId,
                    lastDock.zone || 'center',
                );
            } else {
                root = appendPanelToRoot(root, panelId);
            }
        } else {
            root = appendPanelToRoot(root, panelId);
        }
    }

    return {
        ...state,
        root,
        hiddenPanelIds,
        panels,
    };
}

function appendPanelToRoot(root, panelId) {
    if (!root) return createPanelNode(panelId);
    return createSplitNode('horizontal', [root, createPanelNode(panelId)], [0.7, 0.3]);
}

export function validateLayoutNode(node, registeredPanelIds) {
    if (!node) return true;
    if (node.type === 'panel') {
        return registeredPanelIds.has(node.panelId);
    }
    if (node.type === 'tabs') {
        return (node.panelIds || []).every((id) => registeredPanelIds.has(id));
    }
    if (node.type === 'centerSlot') {
        return !node.child || validateLayoutNode(node.child, registeredPanelIds);
    }
    if (node.type === 'split') {
        return (
            (node.children || []).length >= 1 &&
            node.children.every((ch) => validateLayoutNode(ch, registeredPanelIds))
        );
    }
    return false;
}

export function syncRegionsAfterChange(root, panelZoneMap) {
    return inferRegionsFromTree(root, panelZoneMap);
}

/**
 * Drop a detached group subtree onto a host sub-zone.
 */
export function dockGroupAtTarget(root, regions, detached, targetNodeId, zone) {
    if (!detached) return { root, regions };

    let tree = cloneTree(root);
    const reg = { ...(regions || createEmptyRegionsFromConstants()) };

    const targetInfo = findNode(tree, targetNodeId);
    if (!targetInfo?.node) {
        if (normalizeDockZone(zone) === 'center') {
            return insertSubtreeIntoRegion(tree, reg, 'center', detached);
        }
        return { root: tree, regions: reg };
    }

    const merged = mergeSubtreesAtEdge(targetInfo.node, detached, zone);
    let newRoot = replaceNode(tree, targetInfo.node.id, merged);
    newRoot = pruneTree(newRoot);
    return { root: newRoot, regions: syncRegionsAfterChange(newRoot, {}) };
}

export function dockGroupToRegion(
    root,
    regions,
    detached,
    region,
    panelZoneMap = {},
) {
    if (!detached) return { root, regions };
    const result = insertSubtreeIntoRegion(
        cloneTree(root),
        regions,
        region,
        detached,
        panelZoneMap,
    );
    return {
        root: pruneTree(result.root),
        regions: syncRegionsAfterChange(result.root, {}),
    };
}

export function treeContainsSubtree(root, detached) {
    if (!detached) return true;
    const ids = collectPanelIds(detached);
    return ids.every((id) => treeContainsPanel(root, id));
}
